import { useMemo } from "react";
import { CheckCircle2, PauseCircle } from "lucide-react";
import { formatBigDollars, formatExactDollars } from "../lib/format";

// =============================================================================
// SCOPE-A state update from HUB Design ruling, briefed 19 May 2026.
// SUNSET: this view ends 19 July 2026 — new SMSF Investment View SPOKE opens.
//
// Items from the Update-SMSFPortalTasks.ps1 source script that had no
// matching JS entry (flagged per ruling §8) — left here for CD review:
//   - "Leanne retirement declaration signed"       (no equivalent item)
//   - "Funds moved per Tim's instructions in Macquarie"
//        (regex matches no title; closest intent is the $300k deposit but
//         the deposit milestone title does not match — left untouched)
//   - "TBAR reporting"                              (Tim/Auditor-controlled)
//   - "Insurance review (Leanne)"                   (not in current view)
//
// Overlaps applied dual-effect (per ruling §8 — flagged here):
//   - "Leanne pension commencement pack signed" → marked done on the NOW item
//     AND on the "HARD DEADLINE: Leanne pension commenced in CILK" milestone
//     (backdating to 1/7/25 effectively fulfils the 30 Jun deadline).
//   - "Carl retirement declaration signed" + 19 Jul commencement annotation
//     both land on the single "Sign Carl retirement declaration + pension
//     commencement" milestone. Treated as done + note.
//
// FY27 amount: $845k restored per CD ruling 19 May 2026 (overrides the
// 12 May v2 correction's $650k revision). PATH_TO_TARGET projections
// cascaded accordingly — total deposits now $300k + $845k = $1.145M, so
// per-person 50% contributions = $572,500.
// =============================================================================

const TARGET_AUD = 4_000_000;

// CILK Macquarie Accelerator account balance as of 1 Jul 2026 — confirmed
// figure from Macquarie (replaces the 22 Apr Entity_BalanceSheet provisional).
// Includes the 28 Jun $300k + 1 Jul $845k deposits already applied.
const CURRENT_BALANCE_AUD = 1_324_105;

// Items with no date flexibility — surface above the timeline.
// `done` / `completedDate` mark closed items; the entry still renders but
// shows as completed (struck through + green check). Per HUB Design ruling
// §2.1, completed items keep their vertical space (don't collapse).
const NOW_ITEMS = [
  {
    title: "Sign Leanne pension commencement pack",
    detail: "Backdate to 1 Jul 2025",
    owner: "CD + Leanne",
    account: "CILK – Leanne pension",
    done: true,
    completedDate: "2026-05-20",
  },
  {
    title: "Confirm Leanne withdrawal date with Tim",
    detail: "For re-contribution strategy",
    owner: "Tim",
    account: null,
    done: true,
    completedDate: "2026-05-20",
  },
  {
    title: "Send death benefit forms to Ebony + Alysha",
    detail: null,
    owner: "CD",
    account: "RSF",
    done: true,
    completedDate: "2026-05-20",
  },
  {
    title: "Complete binding death nominations",
    detail: "Carl + Leanne",
    owner: "CD + Leanne",
    account: "CILK",
    done: true,
    completedDate: "2026-05-20",
  },
];

// Type styles match the Gantt legend, plus a "deferred" state added per
// ruling §2.2 for items postponed to a future trigger.
const TYPE_STYLES = {
  deadline: { dot: "bg-red-500", chip: "bg-red-100 text-red-700", label: "Deadline" },
  deposit: { dot: "bg-green-500", chip: "bg-green-100 text-green-700", label: "Deposit" },
  projected: { dot: "bg-amber-500", chip: "bg-amber-100 text-amber-700", label: "Projected" },
  operation: { dot: "bg-slate-400", chip: "bg-slate-100 text-slate-600", label: "Step" },
  deferred: { dot: "bg-slate-300", chip: "bg-slate-200 text-slate-600", label: "Deferred" },
};

const MILESTONES = [
  {
    sortKey: "2026-06-27a",
    date: "Before 28 Jun",
    title: "Leanne withdraws $120k from CILK",
    detail:
      "Round-tripped within the $300k deposit below — NOT additional cash. Adding this as a separate contribution would trigger Leanne's bring-forward rule and block the FY27 strategy.",
    amount: -120_000,
    account: "CILK Leanne → personal",
    owner: "Leanne",
    type: "operation",
    done: true,
    completedDate: "2026-05-20",
  },
  {
    sortKey: "2026-06-28",
    date: "28 Jun 2026",
    title: "Deposit $300k to CILK",
    detail:
      "Includes Leanne $120k NCC (her recontribution) + CD $120k NCC + $30k concessional each",
    amount: 300_000,
    account: "Macquarie personal → CILK",
    owner: "CD",
    type: "deposit",
    done: true,
    completedDate: "2026-05-13",
  },
  {
    sortKey: "2026-06-29",
    date: "Before 30 Jun",
    title: "Minimum pension withdrawal — Leanne",
    detail: null,
    amount: -8_790,
    account: "CILK Leanne pension → personal",
    owner: "Tim sets up",
    type: "operation",
    done: true,
    completedDate: "2026-05-20",
  },
  {
    sortKey: "2026-06-30",
    date: "30 Jun 2026",
    title: "HARD DEADLINE: Leanne pension commenced in CILK",
    detail: null,
    amount: null,
    account: "CILK",
    owner: "Checkpoint",
    type: "deadline",
    done: true,
    completedDate: "2026-05-13",
    note: "Effectively fulfilled — pension pack signed with backdate to 1/7/25.",
  },
  {
    sortKey: "2026-07-01",
    date: "1 Jul 2026",
    title: "Deposit $845k to CILK",
    detail:
      "$390k NCC bring-forward + $32.5k concessional × 2 — restored to Tim's recommended maximum (or post-19 Jul)",
    amount: 845_000,
    account: "Macquarie personal → CILK",
    owner: "CD",
    type: "deposit",
    done: true,
    completedDate: "2026-07-01",
  },
  {
    sortKey: "2026-07-19",
    date: "19 Jul 2026",
    title: "Carl turns 60 — preservation age reached",
    detail: null,
    amount: null,
    account: null,
    owner: "Milestone",
    type: "operation",
  },
  {
    sortKey: "2026-07-20",
    date: "Post-19 Jul",
    title: "Sign Carl retirement declaration + pension commencement",
    detail: "Declaration signed and post-dated to 19 Jul",
    amount: null,
    account: "CILK → Carl pension",
    owner: "CD + Tim",
    type: "operation",
    done: true,
    completedDate: "2026-05-13",
    note: "Tim briefed 13 May 2026. Will action retirement test, pension commencement, and any related steps on 19 July.",
  },
  {
    sortKey: "2026-07-21",
    date: "Post-19 Jul",
    title: "Carl: withdraw + recontribute",
    detail:
      "Deferred pending RSF property sale and rollover of Carl's share to CILK. Revisit at that point.",
    amountLabel: "net $0",
    account: "CILK Carl",
    owner: "CD",
    type: "deferred",
  },
  {
    sortKey: "2027-07-01",
    date: "1 Jul 2027",
    title: "FY28 concessional contributions",
    detail: "$32.5k each",
    amount: 65_000,
    account: "Bucket company → CILK",
    owner: "CD",
    type: "deposit",
  },
  {
    sortKey: "2027-zz1",
    date: "2026–2027",
    title: "RSF property sale — Runway Drive",
    detail: "Sale dependent",
    amount: 2_000_000,
    amountApprox: true,
    account: "RSF fund",
    owner: "Tim + agents",
    type: "projected",
  },
  {
    sortKey: "2027-zz2",
    date: "Post-sale",
    title: "RSF → CILK rollover",
    detail: "Carl's share",
    amount: 950_000,
    amountApprox: true,
    account: "RSF Carl → CILK Carl",
    owner: "Tim",
    type: "projected",
  },
  {
    sortKey: "2027-zz3",
    date: "Post-sale",
    title: "Wind down RSF",
    detail: "Deregister after rollover complete",
    amount: null,
    account: "RSF",
    owner: "Tim",
    type: "projected",
  },
  {
    sortKey: "2030-ongoing",
    date: "Ongoing",
    title: "Beneficiary loan repayments → NCC contributions",
    detail: "As received",
    amountLabel: "up to $1.8M",
    account: "SandeuTrust → personal → CILK",
    owner: "Julie + CD",
    type: "projected",
  },
];

const PATH_TO_TARGET = [
  {
    person: "Carl",
    current: 85_560,
    currentLabel: "$85,560 (CILK) + $333,753 (RSF)",
    additions: [
      {
        label: "Contributions (50% of $1.145M)",
        value: 572_500,
        approx: true,
      },
      { label: "RSF rollover", value: 950_000, approx: true },
      { label: "Ongoing beneficiary loan NCC", value: null },
    ],
    projected: 1_608_000,
    projectedLabel: "~$1.61M",
    target: 2_000_000,
    headroomLabel: "~$392k",
  },
  {
    person: "Leanne",
    current: 219_764,
    currentLabel: "$219,764 (CILK, now pension phase)",
    additions: [
      {
        label: "Contributions (50% of $1.145M)",
        value: 572_500,
        approx: true,
      },
      { label: "Ongoing beneficiary loan NCC", value: null },
    ],
    projected: 792_000,
    projectedLabel: "~$792k",
    target: 2_000_000,
    headroomLabel: "~$1.21M",
  },
];

function formatPct(ratio) {
  return `${Math.round(ratio * 100)}%`;
}

function formatCompletedDate(isoStr) {
  if (!isoStr) return null;
  return new Date(isoStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatMilestoneAmount(m) {
  if (m.amountLabel) return m.amountLabel;
  if (m.amount == null) return null;
  if (m.amount === 0) return "net $0";
  if (m.amountApprox) return `~${formatBigDollars(Math.abs(m.amount))}`;
  const prefix = m.amount > 0 ? "+" : "";
  return `${prefix}${formatBigDollars(m.amount)}`;
}

function milestoneAmountColor(m) {
  if (m.amountApprox || m.amount == null) return "text-slate-700";
  if (m.amount > 0) return "text-green-700";
  if (m.amount < 0) return "text-amber-700";
  return "text-slate-700";
}

function NowCard({ item }) {
  const done = !!item.done;
  return (
    <div
      className="bg-white rounded-2xl border-y border-r border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4"
      style={{ borderLeft: `4px solid ${done ? "#22C55E" : "#EF4444"}` }}
    >
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded ${
          done ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
        }`}
      >
        {done && <CheckCircle2 size={12} strokeWidth={2.5} />}
        {done ? "Done" : "Now"}
      </span>
      <h4
        className={`text-base font-semibold mt-2 leading-snug ${
          done
            ? "text-slate-500 line-through decoration-slate-300"
            : "text-slate-900"
        }`}
      >
        {item.title}
      </h4>
      {item.detail && (
        <p
          className={`text-xs mt-0.5 ${done ? "text-slate-400" : "text-slate-500"}`}
        >
          {item.detail}
        </p>
      )}
      {done && item.completedDate && (
        <p className="text-xs text-green-700 mt-1">
          Completed {formatCompletedDate(item.completedDate)}
        </p>
      )}
      <div className="flex items-center gap-x-3 gap-y-1 mt-2 text-xs flex-wrap">
        <span>
          <span className="text-slate-400">Owner:</span>{" "}
          <span
            className={`font-medium ${done ? "text-slate-500" : "text-slate-900"}`}
          >
            {item.owner}
          </span>
        </span>
        {item.account && (
          <>
            <span className="text-slate-300">·</span>
            <span>
              <span className="text-slate-400">Account:</span>{" "}
              <span
                className={`font-medium ${done ? "text-slate-500" : "text-slate-900"}`}
              >
                {item.account}
              </span>
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function TimelineLegend() {
  const entries = [
    { color: "bg-red-500", label: "Deadline" },
    { color: "bg-green-500", label: "Deposit" },
    { color: "bg-amber-500", label: "Projected / variable" },
    { color: "bg-slate-400", label: "Step" },
    { color: "bg-slate-300", label: "Deferred" },
  ];
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-4 px-1">
      {entries.map((e) => (
        <span
          key={e.label}
          className="inline-flex items-center gap-1.5 text-xs text-slate-600"
        >
          <span className={`w-2.5 h-2.5 rounded-full ${e.color}`} />
          {e.label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
        <CheckCircle2 size={12} className="text-green-600" strokeWidth={2.5} />
        Done
      </span>
    </div>
  );
}

function MilestoneRow({ milestone, isLast }) {
  const ts = TYPE_STYLES[milestone.type] || TYPE_STYLES.operation;
  const done = !!milestone.done;
  const deferred = milestone.type === "deferred";
  const amount = formatMilestoneAmount(milestone);
  const amountColor = milestoneAmountColor(milestone);

  const titleClass = done
    ? "text-sm font-semibold text-slate-500 line-through decoration-slate-300 leading-snug"
    : deferred
    ? "text-sm font-semibold text-slate-700 leading-snug"
    : "text-sm font-semibold text-slate-900 leading-snug";

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center pt-1">
        {done ? (
          <CheckCircle2
            size={18}
            className="text-green-600 shrink-0"
            strokeWidth={2.25}
          />
        ) : (
          <div className={`w-3.5 h-3.5 rounded-full shrink-0 mt-0.5 ${ts.dot}`} />
        )}
        {!isLast && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
      </div>
      <div className="flex-1 pb-5">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-xs text-slate-500">{milestone.date}</p>
          {done ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-green-100 text-green-700">
              <CheckCircle2 size={10} strokeWidth={2.5} /> Done
            </span>
          ) : deferred ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
              <PauseCircle size={10} strokeWidth={2.5} /> Deferred
            </span>
          ) : (
            <span
              className={`inline-block text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${ts.chip}`}
            >
              {ts.label}
            </span>
          )}
        </div>
        <h4 className={titleClass}>{milestone.title}</h4>
        {milestone.detail && (
          <p
            className={`text-xs mt-0.5 ${
              done
                ? "text-slate-400"
                : deferred
                ? "text-slate-700 font-medium"
                : "text-slate-500"
            }`}
          >
            {milestone.detail}
          </p>
        )}
        {milestone.note && (
          <p className="text-xs italic mt-1 text-slate-500">{milestone.note}</p>
        )}
        {done && milestone.completedDate && (
          <p className="text-xs text-green-700 mt-1">
            Completed {formatCompletedDate(milestone.completedDate)}
          </p>
        )}
        <div className="flex items-center gap-x-2 gap-y-1 mt-1.5 text-xs flex-wrap">
          {amount && (
            <span
              className={`font-semibold tabular-nums ${
                done
                  ? "text-slate-400 line-through decoration-slate-300"
                  : amountColor
              }`}
            >
              {amount}
            </span>
          )}
          {amount && (milestone.account || milestone.owner) && (
            <span className="text-slate-300">·</span>
          )}
          {milestone.account && (
            <span className={done ? "text-slate-400" : "text-slate-700"}>
              {milestone.account}
            </span>
          )}
          {milestone.account && milestone.owner && (
            <span className="text-slate-300">·</span>
          )}
          {milestone.owner && (
            <span className={done ? "text-slate-400" : "text-slate-500"}>
              <span className="text-slate-400">Owner:</span> {milestone.owner}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function PathToTargetCard({ path }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {path.person} — CILK Target $2M
      </p>
      <div className="flex items-baseline gap-2 mt-2 flex-wrap">
        <span className="text-2xl sm:text-3xl font-semibold text-[#1B3A5C] tabular-nums">
          {formatExactDollars(path.current)}
        </span>
        <span className="text-slate-400">→</span>
        <span className="text-2xl sm:text-3xl font-semibold text-[#1B3A5C] tabular-nums">
          {path.projectedLabel}
        </span>
      </div>
      <p className="text-xs text-slate-500 mt-1">Current: {path.currentLabel}</p>

      <div className="mt-4 space-y-1.5 text-sm">
        {path.additions.map((a, i) => (
          <div key={i} className="flex justify-between gap-2">
            <span className="text-slate-600 flex items-start gap-1.5">
              <span className="text-slate-400 shrink-0">+</span>
              {a.label}
            </span>
            <span className="text-slate-900 font-medium tabular-nums shrink-0">
              {a.value != null
                ? `${a.approx ? "~" : ""}${formatExactDollars(a.value)}`
                : "—"}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between gap-2 text-sm">
        <span className="text-slate-700 font-medium flex items-start gap-1.5">
          <span className="text-slate-400 shrink-0">=</span>Projected
        </span>
        <span className="text-[#1B3A5C] font-semibold tabular-nums">
          {path.projectedLabel}
        </span>
      </div>
      <p className="text-xs text-slate-400 mt-2">
        $2M cap headroom: {path.headroomLabel}
      </p>
    </div>
  );
}

export default function SmsfPlanView() {
  const sortedMilestones = useMemo(
    () => [...MILESTONES].sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
    []
  );

  // Only sum deposits that aren't yet done — the done ones are already
  // reflected in CURRENT_BALANCE_AUD (the actual Macquarie Accelerator
  // balance), so counting them again would double-count.
  const totalDeposits = useMemo(
    () =>
      sortedMilestones
        .filter((m) => m.type === "deposit" && m.amount > 0 && !m.done)
        .reduce((sum, m) => sum + m.amount, 0),
    [sortedMilestones]
  );
  const projected = CURRENT_BALANCE_AUD + totalDeposits;

  const currentPct = (CURRENT_BALANCE_AUD / TARGET_AUD) * 100;
  const projectedPct = (projected / TARGET_AUD) * 100;
  const currentBarWidth = Math.min(currentPct, 100);
  const projectedBarWidth = Math.max(
    0,
    Math.min(projectedPct, 100) - currentBarWidth
  );

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900 tracking-tight mb-1 px-1">
        SMSF Plan
      </h2>
      <p className="text-xs text-slate-500 mb-4 px-1">
        Source: Tim Beard 7 May 2026, updated 20 May 2026 · WC-SMSF-PLAN
      </p>

      {/* Progress card */}
      <section className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_1px_2px_rgba(15,23,42,0.06)] p-6 mb-6">
        <div className="flex justify-between items-baseline mb-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            SMSF balance
          </p>
          <p className="text-xs text-slate-500 tabular-nums">
            Target {formatBigDollars(TARGET_AUD)}
          </p>
        </div>
        <p className="text-3xl sm:text-4xl font-semibold text-[#1B3A5C] tracking-tight tabular-nums">
          {formatBigDollars(CURRENT_BALANCE_AUD)}
        </p>
        <p className="text-sm text-slate-500 mb-4 tabular-nums">
          {formatPct(CURRENT_BALANCE_AUD / TARGET_AUD)} of target
        </p>

        <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#1B3A5C]"
            style={{ width: `${currentBarWidth}%` }}
          />
          <div
            className="absolute inset-y-0 bg-[#5DA9C7]"
            style={{
              left: `${currentBarWidth}%`,
              width: `${projectedBarWidth}%`,
            }}
          />
        </div>

        <div className="flex items-center gap-3 mt-3 text-xs">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-2 h-2 rounded-sm bg-[#1B3A5C]" />
            Now
          </span>
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-2 h-2 rounded-sm bg-[#5DA9C7]" />
            After planned deposits
          </span>
        </div>

        <p className="text-sm text-slate-700 mt-3 tabular-nums">
          After planned deposits: {formatBigDollars(projected)} (
          {formatPct(projected / TARGET_AUD)})
        </p>
      </section>

      {/* NOW — urgent items */}
      <section className="mb-6">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-red-600 mb-3 px-1">
          Now — no date flexibility
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {NOW_ITEMS.map((item, i) => (
            <NowCard key={i} item={item} />
          ))}
        </div>
      </section>

      {/* Timeline */}
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3 px-1">
        Timeline &amp; money flow
      </h3>
      <TimelineLegend />
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 mb-6">
        {sortedMilestones.map((m, i) => (
          <MilestoneRow
            key={m.sortKey}
            milestone={m}
            isLast={i === sortedMilestones.length - 1}
          />
        ))}
      </div>

      {/* Path to $4M */}
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3 px-1">
        Projected CILK balance — path to $4M
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {PATH_TO_TARGET.map((p) => (
          <PathToTargetCard key={p.person} path={p} />
        ))}
      </div>

      {/* $2M cap rule callout */}
      <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5 mb-3">
        <div className="flex items-start gap-3">
          <span className="text-2xl shrink-0 leading-none">⚠️</span>
          <div>
            <p className="text-sm font-semibold text-amber-900">$2M cap rule</p>
            <p className="text-sm text-amber-800 mt-1 leading-snug">
              Once total super balance reaches $2M per person, non-concessional
              contributions are no longer permitted. Tim&rsquo;s advice: get as
              much cash in as possible NOW while balances are low. This is why
              the June / July deposits are front-loaded.
            </p>
          </div>
        </div>
      </div>

      {/* Julie cross-check callout */}
      <div className="bg-blue-50 rounded-2xl border border-blue-200 p-5">
        <div className="flex items-start gap-3">
          <span className="text-2xl shrink-0 leading-none">🔎</span>
          <div>
            <p className="text-sm font-semibold text-blue-900">
              Julie cross-check required
            </p>
            <p className="text-sm text-blue-800 mt-1 leading-snug">
              Concessional contributions ($30k / $32.5k each) are designed to
              allow dividends from bucket company. Julie needs to validate
              Sandeu income / distribution timing aligns with this strategy.
              Beneficiary loan repayments feeding future NCC contributions also
              need Julie&rsquo;s tax-efficiency sign-off. Schedule review with
              Julie after this plan is finalised.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
